import React, { useState } from 'react'
import {
    Table,
    Tag,
    Button,
    Tooltip,
    Spin,
    Dropdown,
    Menu,
    message
} from 'antd'
import { MoreOutlined, DownOutlined, RightOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { ChadhavaOrderV2, ChildOrder } from '@a4b/api/src/modules/Monetisation/Offerings/types'
import { CustomLoader } from '@/components/Loader'
import RefundModal, { RefundFormData } from '../RefundModal'
import PartialRefundModalChadhava, { PartialRefundFormData } from '../PartialRefundModalChadhava'
import { useAppContext } from '@/components/AppContext'
import { FiltersType } from '../../layouts/ChadhavaOrders'
import { openErrorNotification } from '@/utils'

interface Props {
    orderData: ChadhavaOrderV2[] | undefined
    handleTableChange: (type: string) => void
    filters: FiltersType
    refundHandler: (value: any) => void
    userRole: string
    loading: boolean
    currentPage: number | null
    onRefundSuccess?: () => void
}

const OrderListChadhava: React.FC<Props> = ({
    orderData,
    handleTableChange,
    filters,
    userRole,
    loading,
    currentPage,
    onRefundSuccess
}) => {
    const { networkInstance } = useAppContext()
    const { monetisationOfferingsApi } = networkInstance.clientWithHeaders
    const [refundModal, setRefundModal] = useState<{
        visible: boolean
        order: any | null
        childOrder?: any
        type: 'full' | 'partial'
    }>({
        visible: false,
        order: null,
        type: 'full'
    })

    const [partialRefundModal, setPartialRefundModal] = useState<{
        visible: boolean
        order: ChadhavaOrderV2 | null
        childOrder?: ChildOrder | null
    }>({
        visible: false,
        order: null
    })

    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)
    const [selectedChildOrderId, setSelectedChildOrderId] = useState<string | null>(null)
    const [refundLoading, setRefundLoading] = useState(false)

    const dataSource = orderData

    // Transform ChadhavaOrderV2 data to format expected by refund modals
    const transformOrderForModal = (order: ChadhavaOrderV2, childOrder?: ChildOrder) => {
        const skus = childOrder ? childOrder.sku_name : order.skus
        const transformedOrder = {
            id: childOrder ? childOrder.id : order.id,
            booking_id: childOrder?.booking_id?.toString() || '',
            platform: '',
            user_id: order.user_id.toString(),
            store_id: childOrder?.store_id || order.store_id,
            order_status: '',
            offerings_user_name: '',
            total: childOrder ? childOrder.amount : (order as any).amount || 0, // Use child order amount or parent order amount
            // Add other properties that modals might need
            skus: skus?.map(sku => ({
                inventory_id: sku.id,
                name: sku.name,
                amount: sku.amount,
                refund_amount: sku.amount
            })) || [],
            country_code: order.country_code || 'IN',
        }

        const transformedChildOrder = childOrder ? {
            id: childOrder.id,
            booking_id: childOrder.booking_id.toString(),
            platform: '',
            user_id: order.user_id.toString(),
            store_id: childOrder.store_id,
            order_status: '',
            offerings_user_name: '',
            amount: childOrder.amount, // Add amount property for child order
            skus: skus?.map(sku => ({
                inventory_id: sku.id,
                name: sku.name,
                amount: sku.amount,
                refund_amount: sku.amount
            })) || [],
            other_charges: childOrder.is_other_charge_available,
            total_amount: childOrder.amount,
            is_delivery_charge_available: childOrder.is_delivery_charge_available,
            is_other_charge_available: childOrder.is_other_charge_available,
            country_code: order.country_code || 'IN',
        } : undefined
        return { transformedOrder, transformedChildOrder }
    }

    const handleRefundClick = (order: ChadhavaOrderV2, type: 'full' | 'partial', childOrder?: ChildOrder) => {
        setSelectedOrderId(order.id)
        setSelectedChildOrderId(childOrder?.id || null)

        const { transformedOrder, transformedChildOrder } = transformOrderForModal(order, childOrder)

        if (type === 'partial') {
            let orderDataForPartial = { ...order }
            let childOrderForPartial = childOrder

            // If no childOrder provided (parent order action), create a mock child order from parent order data
            if (!childOrder) {
                const mockChildOrder: ChildOrder = {
                    id: order.id,
                    amount: order.amount || 0,
                    booking_id: order.booking_id || 0,
                    // Use parent order's charge flags when no child order
                    is_delivery_charge_available: order.is_delivery_charge_available || false,
                    is_other_charge_available: order.is_other_charge_available || false,
                    store_id: order.store_id,
                    sku_name: order.skus || [],
                    user_id: order.user_id,
                    order_status: order.order_status
                }
                childOrderForPartial = mockChildOrder
            }
            // If childOrder exists (child order action), use child order's own charge flags

            setPartialRefundModal({
                visible: true,
                order: orderDataForPartial,
                childOrder: childOrderForPartial
            })
        } else {
            setRefundModal({
                visible: true,
                order: transformedOrder as any,
                childOrder: transformedChildOrder as any,
                type
            })
        }
    }

    const handleModalClose = () => {
        setSelectedOrderId(null)
        setSelectedChildOrderId(null)
        setRefundModal({ visible: false, order: null, type: 'full' })
        setPartialRefundModal({ visible: false, order: null })
    }

    const processRefund = async (refundData: RefundFormData) => {
        setRefundLoading(true)
        try {
            // Call V2 Chadhava refund API
            const payload = {
                order_id: refundData.order_id,
                child_order_id: refundData.child_order_id || refundData.order_id,
                skus: refundData.skus?.map(sku => ({
                    inventory_id: sku.inventory_id,
                    amount: sku.amount as number
                })) || [],
                remarks: refundData.remarks,
                type: refundData.type,
                is_delivery_charge_available: refundData.is_delivery_charge_available || false,
                is_other_charge_available: refundData.other_charges || false,
                amount: refundData.amount
            }

            await monetisationOfferingsApi.orderApi.createRefund(refundData.order_id, payload)

            message.success('Refund initiated successfully')
            handleModalClose()
            onRefundSuccess?.()
        } catch (error: any) {
            openErrorNotification(error)

        } finally {
            setRefundLoading(false)
        }
    }

    const handleRefundConfirm = async (refundData: RefundFormData) => {
        await processRefund(refundData)
    }

    const handlePartialRefundConfirm = async (refundData: PartialRefundFormData) => {
        // Convert PartialRefundFormData to RefundFormData for compatibility
        const convertedData: RefundFormData = {
            order_id: refundData.order_id,
            child_order_id: refundData.child_order_id,
            skus: refundData.skus,
            amount: refundData.amount,
            remarks: refundData.remarks,
            type: refundData.type,
            method: 'normal', // default method
            other_charges: refundData.is_other_charge_available,
            is_delivery_charge_available: refundData.is_delivery_charge_available || false,
        }
        await processRefund(convertedData)
    }

    const getRefundMenu = (record: ChadhavaOrderV2, childOrder?: ChildOrder) => {
        // If it's a child order, allow both full and partial refund
        if (childOrder) {
            return (
                <Menu>
                    <Menu.Item
                        key="full"
                        onClick={() => handleRefundClick(record, 'full', childOrder)}
                    >
                        Full Refund
                    </Menu.Item>
                    <Menu.Item
                        key="partial"
                        onClick={() => handleRefundClick(record, 'partial', childOrder)}
                    >
                        Partial Refund
                    </Menu.Item>
                </Menu>
            )
        }

        // For parent orders
        if (record.is_master_order === false && (!record.child_orders || record.child_orders.length === 0)) {
            // Non-master order with no child orders - allow both full and partial refund
            return (
                <Menu>
                    <Menu.Item
                        key="full"
                        onClick={() => handleRefundClick(record, 'full')}
                    >
                        Full Refund
                    </Menu.Item>
                    <Menu.Item
                        key="partial"
                        onClick={() => handleRefundClick(record, 'partial')}
                    >
                        Partial Refund
                    </Menu.Item>
                </Menu>
            )
        } else if (record.is_master_order === true) {
            // Master order - only allow full refund for parent
            return (
                <Menu>
                    <Menu.Item
                        key="full"
                        onClick={() => handleRefundClick(record, 'full')}
                    >
                        Full Refund
                    </Menu.Item>
                </Menu>
            )
        }

        // Default fallback - only full refund
        return (
            <Menu>
                <Menu.Item
                    key="full"
                    onClick={() => handleRefundClick(record, 'full')}
                >
                    Full Refund
                </Menu.Item>
            </Menu>
        )
    }

    const commonColumns = [
        {
            title: 'Order Id',
            dataIndex: 'id',
            key: 'id',
            width: 250,
            render: (id: string) => (
                <Tooltip placement="topLeft" title={id}>
                    <div style={{
                        wordBreak: 'break-all',
                        whiteSpace: 'normal',
                        lineHeight: '1.2'
                    }}>
                        {id}
                    </div>
                </Tooltip>
            ),
        },
        {
            title: 'Booking Id',
            dataIndex: 'booking_id',
            key: 'booking_id',
        },
        {
            title: 'User Id',
            dataIndex: 'user_id',
            key: 'user_id',
            ellipsis: {
                showTitle: false,
            },
            render: (user_id: string) => (
                <Tooltip placement="topLeft" title={user_id}>
                    {user_id}
                </Tooltip>
            ),
        },
        {
            title: 'Store Id',
            dataIndex: 'store_id',
            key: 'store_id',
            width: 250,
            render: (store_id: string) => (
                <Tooltip placement="topLeft" title={store_id}>
                    <div style={{
                        wordBreak: 'break-all',
                        whiteSpace: 'normal',
                        lineHeight: '1.2'
                    }}>
                        {store_id}
                    </div>
                </Tooltip>
            ),
        },
        {
            title: 'Order Status',
            dataIndex: 'order_status',
            key: 'order_status',
            width: 150,
            render: (text: string) => {
                if (text) {
                    return (
                        <Tag color='green' key={text} style={{ width: 'fit-content' }}>
                            {text.toUpperCase()}
                        </Tag>
                    )
                }
                return "-"
            }
        },
        // {
        //     title: 'Action',
        //     dataIndex: 'action',
        //     key: 'action',
        //     render: (text: string, record: ChadhavaOrderV2) => {
        //         return (
        //             <Dropdown
        //                 overlay={getRefundMenu(record)}
        //                 placement="bottomLeft"
        //                 trigger={['click']}
        //             >
        //                 <Button type="text" icon={<MoreOutlined />} />
        //             </Dropdown>
        //         )
        //     }
        // }
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (text: string, record: ChadhavaOrderV2) => {
                if (record?.order_status === 'rescheduled' || record?.order_status === 'refund_initiated' || record?.order_status === 'refunded') {
                    return <Tag color='red'>No Refund</Tag>
                } else if (record?.order_status === 'refunded') {
                    return <Tag color='#00BD68'>
                        Refunded<CheckCircleOutlined style={{ marginLeft: '5px' }
                        } />
                    </Tag >
                } else {
                    return (
                        <Dropdown
                            overlay={getRefundMenu(record)}
                            placement="bottomLeft"
                            trigger={['click']}
                        >
                            <Button type="text" icon={<MoreOutlined />} />
                        </Dropdown>
                    )
                }
            }
        }
    ]

    let columns: any[] = [...commonColumns]

    const expandable = filters?.serviceType === 'offerings' ? {
        expandedRowRender: (record: ChadhavaOrderV2) => {
            if (!record.child_orders || record.child_orders.length === 0) {
                return <div>No child orders available</div>
            }

            // Create child columns with proper action column for child orders
            const childColumns = [...columns]
            // Replace the action column with one that passes childOrder parameter
            childColumns[childColumns.length - 1] = {
                title: 'Action',
                dataIndex: 'action',
                key: 'action',
                render: (text: string, childOrderRecord: ChildOrder) => {
                    if (childOrderRecord?.order_status === 'rescheduled' || childOrderRecord?.order_status === 'refund_initiated' || childOrderRecord?.order_status === 'refunded') {
                        return <Tag color='red'>No Refund</Tag>
                    } else if (childOrderRecord?.order_status === 'refunded') {
                        return <Tag color='#00BD68'>
                            Refunded<CheckCircleOutlined style={{ marginLeft: '5px' }} />
                        </Tag>
                    } else {
                        return (
                            <Dropdown
                                overlay={getRefundMenu(record, childOrderRecord)}
                                placement="bottomLeft"
                                trigger={['click']}
                            >
                                <Button type="text" icon={<MoreOutlined />} />
                            </Dropdown>
                        )
                    }
                }
            }

            return (
                <Table
                    columns={childColumns}
                    dataSource={record.child_orders}
                    pagination={false}
                    rowKey="id"
                    size="small"
                    showHeader={false}
                    rowClassName={(childRecord) => {
                        return selectedChildOrderId === childRecord.id ? 'selected-child-row' : 'default-child-row'
                    }}
                />
            )
        },
        expandIcon: ({ expanded, onExpand, record }: any) =>
            record.child_orders && record.child_orders.length > 0 ? (
                expanded ? (
                    <DownOutlined
                        onClick={(e) => onExpand(record, e)}
                        style={{
                            backgroundColor: '#1890FF',
                            color: 'white',
                            padding: '4px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    />
                ) : (
                    <RightOutlined
                        onClick={(e) => onExpand(record, e)}
                        style={{
                            backgroundColor: '#1890FF',
                            color: 'white',
                            padding: '4px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    />
                )
            ) : null,
        onExpandedRowsChange: (expandedRows: readonly React.Key[]) => {
            setExpandedRowKeys(expandedRows as string[])
        },
    } : undefined

    return (
        <>
            <style>
                {`
                    /* Expanded parent row highlighting */
                    .ant-table-tbody > tr.expanded-parent-row {
                        background-color: #BAE7FF !important;
                    }
                    .ant-table-tbody > tr.expanded-parent-row:hover {
                        background-color: #BAE7FF !important;
                    }
                    .ant-table-tbody > tr.expanded-parent-row > td {
                        background-color: #BAE7FF !important;
                    }
                    .ant-table-tbody > tr.expanded-parent-row:hover > td {
                        background-color: #BAE7FF !important;
                    }

                    /* Selected parent row (when refund is clicked) */
                    .ant-table-tbody > tr.selected-parent-row {
                        background-color: #BAE7FF !important;
                    }
                    .ant-table-tbody > tr.selected-parent-row:hover {
                        background-color: #BAE7FF !important;
                    }
                    .ant-table-tbody > tr.selected-parent-row > td {
                        background-color: #BAE7FF !important;
                    }
                    .ant-table-tbody > tr.selected-parent-row:hover > td {
                        background-color: #BAE7FF !important;
                    }

                    /* Child rows always have light background */
                    .ant-table-expanded-row .ant-table-tbody > tr {
                        background-color: #E6F7FF !important;
                    }
                    .ant-table-expanded-row .ant-table-tbody > tr:hover {
                        background-color: #E6F7FF !important;
                    }
                    .ant-table-expanded-row .ant-table-tbody > tr > td {
                        background-color: #E6F7FF !important;
                    }
                    .ant-table-expanded-row .ant-table-tbody > tr:hover > td {
                        background-color: #E6F7FF !important;
                    }

                    /* Selected child row (when refund is clicked on child) */
                    .ant-table-expanded-row .ant-table-tbody > tr.selected-child-row {
                        background-color: #E6F7FF !important;
                    }
                    .ant-table-expanded-row .ant-table-tbody > tr.selected-child-row:hover {
                        background-color: #E6F7FF !important;
                    }
                    .ant-table-expanded-row .ant-table-tbody > tr.selected-child-row > td {
                        background-color: #E6F7FF !important;
                    }
                    .ant-table-expanded-row .ant-table-tbody > tr.selected-child-row:hover > td {
                        background-color: #E6F7FF !important;
                    }
                `}
            </style>
            <Table
                dataSource={dataSource as any}
                columns={columns}
                expandable={expandable}
                size='large'
                loading={{
                    spinning: loading,
                    indicator: <Spin indicator={<CustomLoader />} />,
                }}
                pagination={false}
                rowKey="id"
                rowClassName={(record) => {
                    let className = ''

                    // If row is expanded, add expanded class
                    if (expandedRowKeys.includes(record.id)) {
                        className += 'expanded-parent-row '
                    }

                    // If row is selected for refund, add selected class
                    if (selectedOrderId === record.id) {
                        className += 'selected-parent-row '
                    }

                    return className.trim()
                }}
            />
            {
                currentPage &&
                <div style={{ display: 'flex', justifyContent: 'right', width: '100%', paddingTop: '10px' }}>
                    <Button onClick={() => handleTableChange('previous')}>Prev</Button>
                    <Button style={{ marginLeft: '5px', marginRight: '5px' }}>{String(currentPage)}</Button>
                    <Button onClick={() => handleTableChange('next')}>Next</Button>
                </div>
            }

            <RefundModal
                visible={refundModal.visible && !!refundModal.order}
                onCancel={handleModalClose}
                onConfirm={handleRefundConfirm}
                order={refundModal.order}
                childOrder={refundModal.childOrder}
                refundType={refundModal.type}
                serviceType="offerings"
                loading={refundLoading}
            />

            <PartialRefundModalChadhava
                visible={partialRefundModal.visible && !!partialRefundModal.order}
                onCancel={handleModalClose}
                onConfirm={handlePartialRefundConfirm}
                order={partialRefundModal.order}
                childOrder={partialRefundModal.childOrder || null}
                loading={refundLoading}
            />
        </>
    )
}

export default OrderListChadhava